// SMSF Enhanced Features UI Component
// Integrates document generation, ATO reporting, and compliance calendar
// Last updated: January 2025

import React, { useState, useEffect } from 'react';
import SMSFDocumentGenerator from '../SMSFDocumentGenerator.js';
import ATOReportingAutomation from '../ATOReportingAutomation.js';
import ComplianceCalendar from '../ComplianceCalendar.js';
import { MobileCard, MobileButton, MobileTabs, useMobileDetection } from './MobileOptimizedUI.js';
import InfoTooltip from './InfoTooltip.js';


const SMSFEnhancedFeatures = ({ smsfData, className = "" }) => {
    const [activeTab, setActiveTab] = useState('documents');
    const [documents, setDocuments] = useState([]);
    const [reports, setReports] = useState([]);
    const [calendar, setCalendar] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const isMobile = useMobileDetection();

    // Initialize data
    useEffect(() => {
        loadComplianceCalendar();
    }, []);

    const loadComplianceCalendar = async () => {
        setIsLoading(true);
        try {
            const calendarData = ComplianceCalendar.getComplianceCalendar();
            setCalendar(calendarData);
        } catch (error) {
            console.error('Failed to load compliance calendar:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // Document Generation Functions
    const generateDocument = async (documentType) => {
        setIsLoading(true);
        try {
            const document = SMSFDocumentGenerator.generateDocument(documentType, smsfData);
            const html = SMSFDocumentGenerator.exportDocument(document, 'html');
            
            setDocuments(prev => [...prev, { ...document, html, generatedAt: new Date().toISOString() }]);
            
            // Show success message
            alert(`${document.documentType} generated successfully!`);
        } catch (error) {
            console.error('Document generation failed:', error);
            alert('Failed to generate document. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    // ATO Reporting Functions
    const generateReport = async (reportType) => {
        setIsLoading(true);
        try {
            const report = ATOReportingAutomation.generateReport(reportType, smsfData);
            const jsonData = ATOReportingAutomation.exportToATOFormat(report, 'json');
            
            setReports(prev => [...prev, { ...report, jsonData, generatedAt: new Date().toISOString() }]);
            
            alert(`${report.documentType} generated successfully!`);
        } catch (error) {
            console.error('Report generation failed:', error);
            alert('Failed to generate report. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    // Document preview
    const previewDocument = (document) => {
        const newWindow = window.open('', '_blank');
        newWindow.document.write(document.html);
        newWindow.document.close();
    };

    // Download document
    const downloadDocument = (document, format = 'html') => {
        const blob = new Blob([document.html], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${document.documentType.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.html`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };
    
    const tabs = [
        { id: 'documents', label: '📄 Documents' },
        { id: 'reports', label: '📋 ATO Reports' },
        { id: 'calendar', label: '📅 Compliance Calendar' }
    ];

    return (
        <div className={`bg-white border border-gray-200 rounded-lg ${className}`}>
            <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    <InfoTooltip content="Enhanced SMSF features including document generation, ATO reporting, and compliance calendar">
                        📋 SMSF Enhanced Features
                    </InfoTooltip>
                </h3>

                <MobileTabs 
                    tabs={tabs}
                    activeTab={activeTab}
                    onTabChange={setActiveTab}
                    className="mb-6"
                />

                {isLoading && (
                    <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        <span className="ml-3 text-gray-600">Generating...</span>
                    </div>
                )}

                {/* Documents Tab */}
                {activeTab === 'documents' && (
                    <div className="space-y-6">
                        <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-6">
                            <h4 className="font-semibold text-gray-900 mb-2">⚠️ Document Templates Currently Unavailable</h4>
                            <p className="text-sm text-gray-700 mb-4">
                                The document template generators (Trust Deed, Trustee Minutes, etc.) are being rebuilt to integrate with the comprehensive member data system.
                            </p>
                            <p className="text-sm text-gray-700 mb-4">
                                For all SMSF reporting needs, please use the <strong>Reports</strong> tab which provides:
                            </p>
                            <ul className="text-sm text-gray-700 list-disc list-inside mb-4 space-y-1">
                                <li>ATO Annual Returns (PDF export)</li>
                                <li>Member Statements (PDF export)</li>
                                <li>Compliance Reports (PDF export)</li>
                                <li>Fee Analysis Reports</li>
                                <li>LRBA Property Reports</li>
                                <li>Division 293 Tax Reports</li>
                                <li>Pension Payment Schedules</li>
                                <li>Tax Loss Harvesting Reports</li>
                            </ul>
                            <button
                                onClick={() => window.dispatchEvent(new CustomEvent('navigate-to-reports'))}
                                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold transition shadow-md"
                            >
                                → Go to Reports Tab
                            </button>
                        </div>
                        
                        <div className="opacity-50 pointer-events-none">
                            <h4 className="font-medium text-gray-900 mb-4">Document Generation (Coming Soon)</h4>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <MobileCard className="p-4 bg-gray-100">
                                    <h5 className="font-medium text-gray-900 mb-2">Trust Deed</h5>
                                    <p className="text-sm text-gray-600 mb-4">Generate SMSF Trust Deed with current regulations</p>
                                    <MobileButton 
                                        onClick={() => {}}
                                        variant="primary"
                                        size="sm"
                                        className="w-full"
                                        disabled
                                    >
                                        Coming Soon
                                    </MobileButton>
                                </MobileCard>

                                <MobileCard className="p-4 bg-gray-100">
                                    <h5 className="font-medium text-gray-900 mb-2">Trustee Minutes</h5>
                                    <p className="text-sm text-gray-600 mb-4">Create trustee meeting minutes template</p>
                                    <MobileButton 
                                        onClick={() => {}}
                                        variant="primary"
                                        size="sm"
                                        disabled
                                        className="w-full"
                                    >
                                        Generate Minutes
                                    </MobileButton>
                                </MobileCard>

                                <MobileCard className="p-4 bg-gray-100">
                                    <h5 className="font-medium text-gray-900 mb-2">Investment Resolution</h5>
                                    <p className="text-sm text-gray-600 mb-4">Create investment decision resolution</p>
                                    <MobileButton 
                                        onClick={() => {}}
                                        variant="primary"
                                        size="sm"
                                        className="w-full"
                                        disabled
                                    >
                                        Coming Soon
                                    </MobileButton>
                                </MobileCard>

                                <MobileCard className="p-4 bg-gray-100">
                                    <h5 className="font-medium text-gray-900 mb-2">Compliance Checklist</h5>
                                    <p className="text-sm text-gray-600 mb-4">Annual compliance checklist</p>
                                    <MobileButton 
                                        onClick={() => {}}
                                        variant="primary"
                                        size="sm"
                                        className="w-full"
                                        disabled
                                    >
                                        Coming Soon
                                    </MobileButton>
                                </MobileCard>
                            </div>
                        </div>

                        {/* Generated Documents */}
                        {documents.length > 0 && (
                            <div>
                                <h4 className="font-medium text-gray-900 mb-4">Generated Documents</h4>
                                <div className="space-y-3">
                                    {documents.map((doc, index) => (
                                        <MobileCard key={index} className="p-4">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <h5 className="font-medium text-gray-900">{doc.documentType}</h5>
                                                    <p className="text-sm text-gray-600">
                                                        Generated: {new Date(doc.generatedAt).toLocaleString()}
                                                    </p>
                                                </div>
                                                <div className="flex space-x-2">
                                                    <MobileButton 
                                                        onClick={() => previewDocument(doc)}
                                                        variant="outline"
                                                        size="sm"
                                                    >
                                                        Preview
                                                    </MobileButton>
                                                    <MobileButton 
                                                        onClick={() => downloadDocument(doc)}
                                                        variant="primary"
                                                        size="sm"
                                                    >
                                                        Download
                                                    </MobileButton>
                                                </div>
                                            </div>
                                        </MobileCard>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* ATO Reports Tab */}
                {activeTab === 'reports' && (
                    <div className="space-y-6">
                        <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-6">
                            <h4 className="font-semibold text-gray-900 mb-2">⚠️ ATO Report Templates Currently Unavailable</h4>
                            <p className="text-sm text-gray-700 mb-4">
                                The ATO report template generators are being rebuilt. For fully functional ATO-compliant reports, please use the <strong>Reports</strong> tab.
                            </p>
                            <button
                                onClick={() => window.dispatchEvent(new CustomEvent('navigate-to-reports'))}
                                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold transition shadow-md"
                            >
                                → Go to Reports Tab
                            </button>
                        </div>
                        
                        <div className="opacity-50 pointer-events-none">
                            <h4 className="font-medium text-gray-900 mb-4">ATO Reporting (Coming Soon)</h4>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <MobileCard className="p-4 bg-gray-100">
                                    <h5 className="font-medium text-gray-900 mb-2">SMSF Annual Return</h5>
                                    <p className="text-sm text-gray-600 mb-4">Generate SMSF Annual Return data for ATO lodgment</p>
                                    <MobileButton 
                                        onClick={() => {}}
                                        variant="primary"
                                        size="sm"
                                        className="w-full"
                                        disabled
                                    >
                                        Coming Soon
                                    </MobileButton>
                                </MobileCard>

                                <MobileCard className="p-4 bg-gray-100">
                                    <h5 className="font-medium text-gray-900 mb-2">Member Contributions</h5>
                                    <p className="text-sm text-gray-600 mb-4">Member contribution reporting for ATO</p>
                                    <MobileButton 
                                        onClick={() => {}}
                                        variant="primary"
                                        size="sm"
                                        className="w-full"
                                        disabled
                                    >
                                        Coming Soon
                                    </MobileButton>
                                </MobileCard>

                                <MobileCard className="p-4 bg-gray-100">
                                    <h5 className="font-medium text-gray-900 mb-2">Compliance Report</h5>
                                    <p className="text-sm text-gray-600 mb-4">Comprehensive compliance monitoring report</p>
                                    <MobileButton 
                                        onClick={() => {}}
                                        variant="primary"
                                        size="sm"
                                        className="w-full"
                                        disabled
                                    >
                                        Coming Soon
                                    </MobileButton>
                                </MobileCard>
                            </div>
                        </div>

                        {/* Generated Reports */}
                        {reports.length > 0 && (
                            <div>
                                <h4 className="font-medium text-gray-900 mb-4">Generated Reports</h4>
                                <div className="space-y-3">
                                    {reports.map((report, index) => (
                                        <MobileCard key={index} className="p-4">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <h5 className="font-medium text-gray-900">{report.documentType}</h5>
                                                    <p className="text-sm text-gray-600">
                                                        Generated: {new Date(report.generatedAt).toLocaleString()}
                                                    </p>
                                                </div>
                                                <div className="flex space-x-2">
                                                    <MobileButton 
                                                        onClick={() => {
                                                            const blob = new Blob([report.jsonData], { type: 'application/json' });
                                                            const url = URL.createObjectURL(blob);
                                                            const a = document.createElement('a');
                                                            a.href = url;
                                                            a.download = `${report.documentType.replace(/\s+/g, '_')}.json`;
                                                            a.click();
                                                            URL.revokeObjectURL(url);
                                                        }}
                                                        variant="primary"
                                                        size="sm"
                                                    >
                                                        Download
                                                    </MobileButton>
                                                </div>
                                            </div>
                                        </MobileCard>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Compliance Calendar Tab */}
                {activeTab === 'calendar' && calendar && (
                    <div className="space-y-6">
                        <div>
                            <h4 className="font-medium text-gray-900 mb-4">Compliance Calendar</h4>
                            
                            {/* Summary Cards */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                                <MobileCard className="p-4 text-center">
                                    <div className="text-2xl font-bold text-blue-600">{calendar.dashboard.summary.total}</div>
                                    <div className="text-sm text-gray-600">Total Tasks</div>
                                </MobileCard>
                                <MobileCard className="p-4 text-center">
                                    <div className="text-2xl font-bold text-green-600">{calendar.dashboard.summary.completed}</div>
                                    <div className="text-sm text-gray-600">Completed</div>
                                </MobileCard>
                                <MobileCard className="p-4 text-center">
                                    <div className="text-2xl font-bold text-red-600">{calendar.dashboard.summary.overdue}</div>
                                    <div className="text-sm text-gray-600">Overdue</div>
                                </MobileCard>
                                <MobileCard className="p-4 text-center">
                                    <div className="text-2xl font-bold text-yellow-600">{calendar.dashboard.summary.upcoming}</div>
                                    <div className="text-sm text-gray-600">Upcoming</div>
                                </MobileCard>
                            </div>

                            {/* Compliance Score */}
                            <MobileCard className="p-4 mb-6">
                                <h5 className="font-medium text-gray-900 mb-2">Compliance Score</h5>
                                <div className="flex items-center">
                                    <div className="flex-1 bg-gray-200 rounded-full h-4 mr-4">
                                        <div 
                                            className="bg-green-600 h-4 rounded-full transition-all duration-300"
                                            style={{ width: `${calendar.dashboard.complianceScore}%` }}
                                        ></div>
                                    </div>
                                    <span className="text-lg font-bold text-gray-900">{calendar.dashboard.complianceScore}%</span>
                                </div>
                            </MobileCard>

                            {/* Overdue Tasks */}
                            {calendar.dashboard.overdue.length > 0 && (
                                <div>
                                    <h5 className="font-medium text-red-600 mb-3">⚠️ Overdue Tasks</h5>
                                    <div className="space-y-2">
                                        {calendar.dashboard.overdue.map((deadline, index) => (
                                            <MobileCard key={index} className="p-3 border-l-4 border-red-500">
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <h6 className="font-medium text-gray-900">{deadline.task}</h6>
                                                        <p className="text-sm text-gray-600">
                                                            Due: {deadline.dueDate.toLocaleDateString()}
                                                        </p>
                                                    </div>
                                                    <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs">
                                                        {deadline.priority}
                                                    </span>
                                                </div>
                                            </MobileCard>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Upcoming Tasks */}
                            {calendar.dashboard.upcoming.length > 0 && (
                                <div>
                                    <h5 className="font-medium text-gray-900 mb-3">📅 Upcoming Tasks (Next 30 Days)</h5>
                                    <div className="space-y-2">
                                        {calendar.dashboard.upcoming.slice(0, 5).map((deadline, index) => (
                                            <MobileCard key={index} className="p-3 border-l-4 border-blue-500">
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <h6 className="font-medium text-gray-900">{deadline.task}</h6>
                                                        <p className="text-sm text-gray-600">
                                                            Due: {deadline.dueDate.toLocaleDateString()}
                                                        </p>
                                                    </div>
                                                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                                                        {deadline.priority}
                                                    </span>
                                                </div>
                                            </MobileCard>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Categories */}
                            <div>
                                <h5 className="font-medium text-gray-900 mb-3">📊 Compliance by Category</h5>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {calendar.dashboard.categories.map((category, index) => (
                                        <MobileCard key={index} className="p-4">
                                            <h6 className="font-medium text-gray-900 mb-2">{category.name}</h6>
                                            <div className="space-y-1 text-sm">
                                                <div className="flex justify-between">
                                                    <span>Total:</span>
                                                    <span>{category.total}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span>Completed:</span>
                                                    <span className="text-green-600">{category.completed}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span>Overdue:</span>
                                                    <span className="text-red-600">{category.overdue}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span>Upcoming:</span>
                                                    <span className="text-blue-600">{category.upcoming}</span>
                                                </div>
                                            </div>
                                        </MobileCard>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SMSFEnhancedFeatures;
